// Mock data for Accu Desk powered by Saber Accounting

export const brand = {
  name: 'Accu Desk',
  tagline: 'powered by Saber Accounting',
  short: 'AD',
};

export const heroStats = [
  { value: '500+', label: 'Practices onboarded', sub: 'and counting' },
  { value: '14hrs', label: 'Saved weekly per user', sub: 'on average' },
  { value: '91%', label: 'Deadlines met early', sub: 'every quarter' },
  { value: 'R600k', label: 'Extra revenue potential', sub: 'per user' },
];

export const trustedLogos = [
  'Advoca Partners',
  'BVDM Group',
  'FinAcc SA',
  'Nedford & Co.',
  'Kruger Audit',
  'Sable Bookkeepers',
];

export const whyFeatures = [
  {
    title: 'Never miss another deadline',
    body: 'Due dates auto-populate based on local regulatory calendars, with day counters per client.',
    icon: 'CalendarClock',
  },
  {
    title: 'Replace four tools with one',
    body: 'Tasks, audits, time and billing live together — no more manual syncing of data between systems.',
    icon: 'Layers',
  },
  {
    title: 'Keep your whole team in sync',
    body: 'Live dashboards, in-task chat and automated emails replace WhatsApp threads and manual emails.',
    icon: 'Users',
  },
  {
    title: 'POPIA-aligned, SA-based support',
    body: 'Encrypted, role-based access per user with a South-African support team who understand local compliance.',
    icon: 'ShieldCheck',
  },
];

export const platformFeatures = [
  {
    tag: 'WORKFLOWS',
    title: 'Automated Task & Workflow Management',
    body: 'Set up clients once. Tasks auto-schedule from SARS & CIPC regulatory dates — forever.',
    icon: 'Workflow',
  },
  {
    tag: 'BILLING',
    title: 'Streamlined Timekeeping & Billing',
    body: 'Hours tracked as you work. One-click invoice generation linked to Sage & Xero. Get paid on time.',
    icon: 'Receipt',
  },
  {
    tag: 'AUDIT',
    title: 'Audit Management',
    body: 'Schedule audits, assign clerks, and track progress section by section. Purpose-built for local compliance.',
    icon: 'ClipboardCheck',
  },
  {
    tag: 'REPORTS',
    title: 'Overviews & Reports',
    body: 'Real-time dashboards and a weekly email summary so nothing slips through the cracks.',
    icon: 'BarChart3',
  },
  {
    tag: 'COMPLIANCE',
    title: 'SARS & CIPC-Driven Deadlines',
    body: 'Built-in day counters on every task. Always know exactly what\u2019s due and when.',
    icon: 'Landmark',
  },
  {
    tag: 'COLLABORATION',
    title: 'Collaboration & Training',
    body: 'One system keeping your entire team in sync — with hands-on training built in.',
    icon: 'MessagesSquare',
  },
];

export const audiences = [
  {
    title: 'Large-Scale Practices',
    body: 'Multi-partner firms managing thousands of clients across tax, audit, and secretarial services.',
    size: 'lg',
  },
  {
    title: 'Small Firms',
    body: 'Lean teams that want to scale but cannot yet hire more staff. Accu Desk provides breathing room and scales with you.',
    size: 'md',
  },
  {
    title: 'Internal Accounting Teams',
    body: 'In-house compliance tracking keeps group entities compliant and on schedule.',
    size: 'md',
  },
  {
    title: 'Auditors',
    body: 'Schedule, plan and track your audits with ease. Easily see where staff are double booked.',
    size: 'md',
  },
  {
    title: 'Local Firms, Global Clients',
    body: 'Practices serving cross-border clients who need multi-jurisdiction compliance.',
    size: 'lg',
  },
];

export const localFeatures = [
  { title: 'SARS & CIPC Deadlines', body: 'Every key date is loaded. Day counters, escalation windows, and automated reminders built in from day one.', icon: 'CalendarDays' },
  { title: 'Beneficial Ownership', body: 'All company information, organograms and deadlines are managed entirely inside Accu Desk.', icon: 'Network' },
  { title: 'Share Register & Certs', body: 'Manage your live share register and generate share certificates in seconds.', icon: 'FileBadge' },
  { title: 'Local SA Support', body: 'Alberton, Johannesburg-based team who understand SARS, CIPC, and everything in between.', icon: 'Headphones' },
];

export const testimonials = [
  { quote: 'Accu Desk literally saved my practice. The best CRM, job manager, task manager and so much more in one place.', name: 'Thandi M.', role: 'Managing Partner, Cape Town' },
  { quote: 'We used to spend hours chasing who\u2019s done what. Now it\u2019s all there \u2014 clear, live and shared.', name: 'Pieter V.', role: 'Audit Manager, Pretoria' },
  { quote: 'I open Accu Desk first thing in the morning and close it last thing at night. It is my practice.', name: 'Nomsa D.', role: 'Sole Practitioner, JHB' },
  { quote: 'The Saber Accounting team has been a delight to work with \u2014 they truly understand SA firms.', name: 'James O.', role: 'Partner, Durban' },
  { quote: 'We handle 2,500 clients; without Accu Desk this simply wouldn\u2019t be possible.', name: 'Riaan B.', role: 'Director, Bloemfontein' },
];

export const plans = [
  {
    name: 'Accu Desk',
    price: 'R399',
    period: '/month, primary user',
    addOn: '+ R149/month per additional user',
    tagline: 'One plan. Every feature. Priced to grow with your team.',
    description: 'The full Accu Desk platform — SARS & CIPC deadlines, time & billing, audit management, templates and reporting — for every user in your firm.',
    features: [
      'All SARS & CIPC deadline automation',
      'Client & compliance management (VAT, PAYE, CIPC)',
      'Time tracking, timesheets & unbilled hours',
      'One-click invoicing linked to Sage & Xero',
      'Audit planning & tracking',
      'Document templates with client-merge',
      'Team collaboration & role-based access',
      'Live dashboards & reports',
      'Beneficial ownership & share registers',
      'SA-based support & onboarding',
    ],
    cta: 'Start Free Trial',
    highlighted: true,
    badge: 'Everything Included',
  },
  {
    name: 'Custom',
    price: 'Custom',
    period: 'Talk to sales',
    tagline: 'For larger firms that need flexibility, collaboration and hands-on support.',
    description: 'Enterprise onboarding, migration support, priority SLAs and dedicated account management for firms with unique requirements.',
    features: [
      'Everything in Accu Desk',
      'Dedicated Account Manager',
      'Fast-tracked onboarding & data migration',
      'On-site training for your team',
      'Priority support with SLA',
      'Custom integrations & workflows',
    ],
    cta: 'Contact Sales',
    highlighted: false,
    contact: true,
  },
];

export const resources = [
  { title: 'Beyond the Spreadsheet | Practice Management System', tag: 'Guide', excerpt: 'Why South African firms are moving beyond spreadsheets to a single, connected practice management platform.' },
  { title: 'The 5 Best Practice Management Tools for SA Firms', tag: 'Comparison', excerpt: 'A hands-on comparison of the top practice management systems built for South African accountants in 2026.' },
  { title: 'SARS Filing Season Calendar 2026', tag: 'Template', excerpt: 'A ready-to-use SARS filing calendar with every provisional, income tax and VAT deadline for the year.' },
];

export const faqs = [
  { q: 'How does Accu Desk handle SARS & CIPC deadlines?', a: 'Accu Desk schedules SARS and CIPC tasks based on regulatory deadlines and provides dedicated tables to track escalation dates. Accu Desk integrates with SARS for correspondence and to request and submit IRP6s.' },
  { q: 'Does it integrate with Microsoft 365?', a: 'Yes. Accu Desk uses a Microsoft 365 integration so automated emails can be sent directly from your inbox and your unique company email address.' },
  { q: 'How long does setup take?', a: 'Onboarding is fast and flexible. Setup can take anywhere from a few hours to a few weeks, depending on the quality of your existing data. You can import clients directly from SARS, CIPC, Sage and Xero \u2014 so you can be up and running almost immediately.' },
  { q: 'Is there a free trial?', a: 'Yes. Accu Desk offers a free 30-day trial designed to ensure you experience the full power of the system. No credit card required.' },
  { q: 'Is my data secure?', a: 'Accu Desk is POPIA-aligned with encrypted, role-based access per user. Your data is hosted on secure infrastructure with regular backups and SA-based support.' },
];

export const images = {
  heroDashboard: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhY2NvdW50YW50JTIwZGFzaGJvYXJkfGVufDB8fHx8MTc5MDc0Nzg4NXww&ixlib=rb-4.1.0&q=85',
  metrics: 'https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxhY2NvdW50YW50JTIwZGFzaGJvYXJkfGVufDB8fHx8MTc5MDc0Nzg4NXww&ixlib=rb-4.1.0&q=85',
  audit: 'https://images.unsplash.com/photo-1626266061368-46a8f578ddd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxhY2NvdW50YW50JTIwd29ya2luZ3xlbnwwfHx8fDE3OTA3NDc4OTB8MA&ixlib=rb-4.1.0&q=85',
  team: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzkwNzQ3OTAzfDA&ixlib=rb-4.1.0&q=85',
  collab: 'https://images.pexels.com/photos/7693692/pexels-photo-7693692.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  review: 'https://images.pexels.com/photos/8297016/pexels-photo-8297016.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
};

// Mock dashboard data (only used post-login)
export const dashboardData = {
  user: { name: 'Sarah Nkosi', firm: 'Nkosi & Partners', role: 'Managing Partner' },
  kpis: [
    { label: 'Open Tasks', value: 47, delta: '+6 today', tone: 'default' },
    { label: 'Due This Week', value: 12, delta: '3 overdue', tone: 'warn' },
    { label: 'Unbilled Hours', value: '38.5h', delta: 'R42,350', tone: 'default' },
    { label: 'Clients Active', value: 214, delta: '+4 this month', tone: 'good' },
  ],
  deadlines: [
    { client: 'Sandton Holdings Ltd', task: 'VAT201 Submission', due: 'Today', days: 0 },
    { client: 'Blue Peak Traders', task: 'IRP6 Provisional', due: 'Tomorrow', days: 1 },
    { client: 'Karoo Farms (Pty) Ltd', task: 'Annual Return CIPC', due: 'In 3 days', days: 3 },
    { client: 'Coastline Logistics', task: 'ITR14 Filing', due: 'In 5 days', days: 5 },
    { client: 'Umhlanga Retail', task: 'PAYE / SDL / UIF', due: 'In 7 days', days: 7 },
  ],
  tasks: [
    { id: 1, title: 'Review Q3 management accounts', client: 'Sandton Holdings Ltd', assignee: 'You', status: 'In Progress' },
    { id: 2, title: 'Prepare audit lead sheets', client: 'Blue Peak Traders', assignee: 'Themba K.', status: 'To Do' },
    { id: 3, title: 'Send draft invoice for approval', client: 'Karoo Farms', assignee: 'You', status: 'Review' },
    { id: 4, title: 'CIPC director change filing', client: 'Coastline Logistics', assignee: 'Amina P.', status: 'To Do' },
    { id: 5, title: 'Reconcile Xero trial balance', client: 'Umhlanga Retail', assignee: 'You', status: 'In Progress' },
  ],
};
